import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useRetainedState } from "@appretio/shared";
import type { CellValueChangedEvent } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore
} from "react";
import type { AlgorithmicForecastLoad } from "../../components/algorithmicForecastDialog/algorithmicForecastLoad";
import type { CubeWindow } from "../../components/cubeDataTable/utils/columnUtils";
import {
  hasIntakeRows,
  type IntakeSource
} from "../../components/cubeDataTable/utils/intakeUtils";
import {
  type ForecastTableControlOptions,
  type ForecastTableControlValues
} from "../../components/tableControls/forecastTableControlValues";
import {
  type CubePage,
  pageForProducer,
  pageScope
} from "../useCubeSelectionState";
import {
  type FetchForecastDataParams,
  type ForecastGridDataOutput,
  useFetchForecastData
} from "../useFetchForecastData";
import {
  type ForecastMetaDataType,
  useFetchForecastMetaData
} from "../useFetchForecastMetaData";
import { interleaveAlgoRows } from "./interleaveAlgoRows";
import {
  type AlgoRow,
  useAlgorithmicForecastState
} from "./useAlgorithmicForecastState";
import { type BatchStartEvent } from "./useCellEditBatcher";
import {
  type GridStateRetention,
  useGridStateRetention
} from "./useGridStateRetention";
import { useHandleApply } from "./useHandleApply";
import { useWorkspaceActions } from "./useWorkspaceActions";
import { useWorkspaceIntake } from "./useWorkspaceIntake";
import {
  type GridWrite,
  useWorkspaceMutationQueue
} from "./useWorkspaceMutationQueue";
import {
  type AppliedCubeFilters,
  useWorkspaceTableState,
  type WorkspaceTableState
} from "./useWorkspaceTableState";

/**
 * Producer scope for cube reads/writes. ``"user"`` for the forecast page,
 * ``"budget"`` for the budget page.
 */
export type CubeProducer = "user" | "budget";

/**
 * Inputs that drive a temporal-grid workspace. The caller owns selection
 * state (contexts, cube ids) and renders all UI; this hook owns the
 * load/save/edit/intake machinery and the table-control state.
 */
export interface UseForecastWorkspaceParams {
  /** Producer scope this workspace operates on. */
  producer: CubeProducer;
  /** Flat list of cube identifiers for the grid data endpoint. */
  cubeIds: string[];
  /** Cube id Apply loads and snapshots as the writable cube. */
  saveCubeId: string | undefined;
  /** The save cube's week window, as the versions payload ships it. */
  saveCubeWindow?: CubeWindow;
  /** Whether the save cube is frozen (budget only). */
  isFrozen?: boolean;
  /** Currently selected (unapplied) context options. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Store-status-resolved locations to merge into context on Apply. */
  resolvedStoreLocations: string[];
  /** Ref to the AG Grid instance (owned by the page that renders it). */
  gridRef: RefObject<AgGridReact | null>;
}

/** Return shape of {@link useForecastWorkspace}. */
export interface ForecastWorkspace {
  // --- Data ---
  /** Grid data from the most recent successful fetch. */
  forecastData: ForecastGridDataOutput | null;
  /** Metadata (year mapping etc.) used for grid-data extraction. */
  metaData: ForecastMetaDataType | undefined;
  /** Rows for the currently-selected calendar view. */
  rowData: Array<Record<string, unknown>>;
  /** True when the current grid contains any INTAKE rows. */
  hasIntakeData: boolean;
  /** The intake sources with rows to swap in. */
  availableIntakeSources: IntakeSource[];
  /**
   * The intake source whose rows the grid shows. It follows the grid, not
   * the ``intake`` control: after a failed swap the grid rolls back while the
   * control keeps the source it asked for. Save sends this source's rows, so
   * whatever gates an intake edit must read it too.
   */
  gridSource: IntakeSource;

  // --- Loading flags ---
  isFetching: boolean;
  /** True while forecast metadata is loading. */
  isMetaDataFetching: boolean;
  isSaving: boolean;
  /**
   * True from the first edit until a save or a reload. Edits are the planner's
   * cell writes, copies and Fill in OTB; an intake swap is not one.
   */
  hasUnsavedEdits: boolean;
  fillInOtbPending: boolean;
  intakePending: boolean;
  /**
   * True while a cell write or intake swap is recomputing the grid. A save
   * then sends a half-cascaded grid, so Save waits and save-first refuses.
   */
  isRecomputing: boolean;
  restateFPOSPending: boolean;

  // --- Applied state ---
  /** Filters used by the most recent Apply click. */
  appliedFilters: AppliedCubeFilters;
  /** True when the user clicked Apply with no cube id selected. */
  hasVersionError: boolean;

  // --- Persistence indicator ---
  /** Server-reported timestamp of the last successful save. */
  savedAt: string | null;

  // --- Toast / dialog state ---
  toastState: WorkspaceTableState["toastState"];
  /** Raises a toast. A copy reports what it wrote and what it skipped here. */
  setToastState: WorkspaceTableState["setToastState"];
  setToastOpen: (open: boolean) => void;
  restateFPOSDialogOpen: boolean;
  setRestateFPOSDialogOpen: (open: boolean) => void;

  // --- Table controls ---
  tableControlOptions: ForecastTableControlOptions;
  setTableControlOptions: Dispatch<SetStateAction<ForecastTableControlOptions>>;
  tableControls: ForecastTableControlValues;
  setTableControls: Dispatch<SetStateAction<ForecastTableControlValues>>;

  // --- Handlers ---
  handleApply: () => void;
  /** Merges a load's per-model rows into the grid beside the ones already held (the load dialog's `onLoaded`); a model's new load replaces its earlier rows. */
  addAlgoForecast: (loads: AlgorithmicForecastLoad[]) => void;
  /** The held loads' per-KPI failures, shown beside the rows that did load. */
  algoFailures: string[];
  handleSave: () => Promise<void>;
  /** ``handleSave`` that rejects when nothing was saved. */
  saveGrid: () => Promise<void>;
  handleCellValueChanged: (event: CellValueChangedEvent) => void;
  /** Buffers a paste's or range delete's cell changes so the cascade runs once they have landed. */
  handleBatchStart: (event: BatchStartEvent) => void;
  /** Flushes the buffered cell changes as a single recalculation batch. */
  handleBatchEnd: () => void;
  /**
   * Queues a batch the grid raised no events for — the copy path. `false`
   * while a write is in flight: nothing was queued.
   */
  queueWrite: (write: GridWrite) => boolean;
  /** True while a batch is being applied. Copy and Save wait for it. */
  isWriting: boolean;
  handleFillInOtb: () => Promise<void>;
  handleRestateFPOS: () => Promise<boolean>;
  /**
   * Clears the loaded grid data — returning the page to its "Select a
   * version and click Apply" empty state — but only when ``cubeId`` is one
   * of the cubes the grid was actually loaded from. Called with the just-
   * deleted cube's id so dropping the loaded cube empties the grid, while
   * deleting a different, merely-selected version leaves a still-valid
   * applied view intact.
   */
  clearForecastDataIfLoadedFrom: (cubeId: string | undefined) => void;

  /**
   * Grid-state retention handle for ``ForecastDataTable``: keeps the user's
   * column layout, hand-set filters and unsaved edits alive across
   * calendar-family switches and unmount/remount.
   */
  gridStateRetention: GridStateRetention;
}

/**
 * Selects the grid rows for the active calendar view. Extracted as a pure
 * module-level helper to keep ``useForecastWorkspace`` within its line budget.
 *
 * ``algoRows`` (the algorithmic-forecast dialog's result rows) are merged in
 * for the Inv 454 view only — the view they were generated against, and the
 * only one Copy is enabled for (`canSave`'s same rule). Held as state and
 * re-derived here rather than applied via ``applyTransaction``, so a
 * calendar-view toggle keeps them.
 */
function selectRowData(
  forecastData: ForecastGridDataOutput | null,
  calendarView: ForecastTableControlValues["calendarView"],
  algoRows: AlgoRow[]
) {
  switch (calendarView) {
    case "Inv 454":
      return interleaveAlgoRows(forecastData?.fiscal_grid_data ?? [], algoRows);
    case "Fin greg":
      return forecastData?.fiscal_gregorian_data ?? [];
    case "Greg cal year":
      return forecastData?.gregorian_grid_data ?? [];
  }
}

/**
 * Subscribe across mounts so a late transaction reaches the visible grid.
 * The patch is laid over the interleaved rows: an edited row comes back as
 * the object the grid already holds, so a load leaves the edits alone, and an
 * algorithmic row never has a patch entry (its producer is in the row id).
 */
function useWorkspaceRows(
  forecastData: ForecastGridDataOutput | null,
  calendarView: ForecastTableControlValues["calendarView"],
  algoRows: AlgoRow[],
  retention: GridStateRetention
) {
  const patch = useSyncExternalStore(
    retention.subscribeRows,
    retention.getRowPatch
  );
  const rowData = useMemo(
    () =>
      retention.patchRows(
        selectRowData(forecastData, calendarView, algoRows),
        calendarView,
        patch
      ),
    [forecastData, calendarView, algoRows, retention, patch]
  );
  return { rowData, hasIntakeData: useHasIntakeData(rowData) };
}

/**
 * The Apply fetch plus a retained copy of its latest success, and the
 * algorithmic loads held beside it (``useAlgorithmicForecastState``). The copy keeps
 * the grid rendering while a follow-up Apply is in flight (``mutation.data``
 * resets to ``undefined`` the moment a new mutate call starts) and brings the
 * loaded grid back after navigating away. New data is served straight from
 * ``latest`` on the render it lands, so the effect's one-frame lag in
 * updating the copy is never visible.
 *
 * Dropping the retained row patch and the held loads belongs here rather
 * than in each caller: every fetch replaces the rows the patch was laid over
 * and the loads sat beside, so a caller that forgot would write pre-fetch
 * edits back into the cube on the next save.
 * A successful replacement invalidates cascades for the previous rows.
 * A failed or unmounted Apply leaves those transactions valid.
 *
 * React drops the state an unmounted Apply writes — the copy above, the
 * applied filters — so the whole Apply rolls back. A write straight to the
 * retained registry would not, leaving the page it comes back to holding
 * pre-Apply rows with the edits gone: every such write after the await is
 * guarded by ``mountedRef``. So is the unsaved-edits flag, which describes
 * the patch.
 */
function useRetainedForecastData(
  page: CubePage,
  retention: GridStateRetention,
  mountedRef: RefObject<boolean>,
  weekLabels: ForecastTableControlValues["weekLabels"]
) {
  const algo = useAlgorithmicForecastState(page, weekLabels);
  const { clearAlgoForecast } = algo;
  const { data: latest, isFetching, fetch, reset } = useFetchForecastData();
  const [retained, setRetained] =
    useRetainedState<ForecastGridDataOutput | null>(
      pageScope(page),
      "forecastData",
      null
    );
  const [hasUnsavedEdits, setHasUnsavedEdits] = useRetainedState(
    pageScope(page),
    "hasUnsavedEdits",
    false
  );
  useEffect(() => {
    if (latest) setRetained(latest);
  }, [latest, setRetained]);

  const fetchForecastData = useCallback(
    async (params: FetchForecastDataParams) => {
      const data = await fetch(params);
      if (mountedRef.current) {
        retention.clearRows();
        clearAlgoForecast();
        setHasUnsavedEdits(false);
      }
      return data;
    },
    [fetch, retention, mountedRef, clearAlgoForecast, setHasUnsavedEdits]
  );

  return {
    forecastData: latest ?? retained,
    isFetching,
    fetchForecastData,
    algo,
    hasUnsavedEdits,
    setHasUnsavedEdits,
    // Both the mutation data and the retained copy pin forecastData; the
    // held loads described that data.
    clearForecastData() {
      setRetained(null);
      reset();
      algo.clearAlgoForecast();
      setHasUnsavedEdits(false);
    }
  };
}

/** True while the owner is mounted. */
function useMountedRef(): RefObject<boolean> {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return mountedRef;
}

/** Whether the rows on screen carry any INTAKE metric. */
function useHasIntakeData(rowData: Array<Record<string, unknown>>) {
  return useMemo(() => hasIntakeRows(rowData), [rowData]);
}

/**
 * Index / per-SQM / SH% / option-derived cells read OTHER rows via the grid
 * api — a cross-row dependency ag-Grid's change detection can't see. An Apply
 * reload swaps rowData under stable column defs + row ids, so those cells keep
 * values computed against the previous load (field report: a new brand shown
 * indexed against the old brand's planning year). Force a full recompute once
 * per reload. Keyed on forecastData — the data itself, so it fires when the
 * new rows have actually landed (not when Apply is clicked), and edits, which
 * don't change forecastData, keep their cell-flash animation intact.
 *
 * The producer badge has the same blind spot within its own row: it renders
 * the diagnostics, but its cell value is the producer string, which an
 * algorithmic reload (same model, new training range) leaves unchanged — so
 * the badge kept the previous WAPE while the tooltip, built fresh on hover,
 * showed the new one. Keyed on algoRows too, so a load refreshes it.
 */
function useRefreshCellsOnReload(
  forecastData: ForecastGridDataOutput | null,
  algoRows: AlgoRow[],
  gridRef: RefObject<AgGridReact | null>
) {
  useEffect(() => {
    if (!forecastData && !algoRows.length) return;
    const api = gridRef.current?.api;
    api?.refreshCells({ force: true, suppressFlash: true });
  }, [forecastData, algoRows, gridRef]);
}

/** The queue's two entry points for planner edits, each marking the grid edited. */
function useEditTracking(
  queue: ReturnType<typeof useWorkspaceMutationQueue>,
  setHasUnsavedEdits: Dispatch<SetStateAction<boolean>>
) {
  const { handleCellValueChanged: queueCellChange, queueWrite: queueBatch } =
    queue;
  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      setHasUnsavedEdits(true);
      queueCellChange(event);
    },
    [queueCellChange, setHasUnsavedEdits]
  );
  const queueWrite = useCallback(
    (write: GridWrite) => {
      const queued = queueBatch(write);
      if (queued) setHasUnsavedEdits(true);
      return queued;
    },
    [queueBatch, setHasUnsavedEdits]
  );
  return { handleCellValueChanged, queueWrite };
}

/**
 * Composable workspace shared by the forecast and budget temporal pages.
 *
 * Owns the apply/save/restate/intake-toggle/fill-in-otb/mutation-queue
 * machinery — the parts that were duplicated almost verbatim between
 * ``ForecastPage`` and ``BudgetPage``. The caller still renders the UI
 * and owns producer-specific selection state (versions, brand, etc.).
 *
 * The intake cache effect intentionally depends only on ``forecastData``:
 * it must run exactly once per Apply-triggered reload.
 */
export function useForecastWorkspace(
  params: UseForecastWorkspaceParams
): ForecastWorkspace {
  const page = pageForProducer(params.producer);
  const { data: metaData, isFetching: isMetaDataFetching } =
    useFetchForecastMetaData();

  const tableState = useWorkspaceTableState(page);
  const { calendarView, weekLabels } = tableState.tableControls;
  const retention = useGridStateRetention({ page, gridRef: params.gridRef });
  const mountedRef = useMountedRef();
  const data = useRetainedForecastData(page, retention, mountedRef, weekLabels);
  const { forecastData, fetchForecastData, algo } = data;
  const queue = useWorkspaceMutationQueue({
    metaData,
    markdownStatus: tableState.appliedFilters.context?.markdown_status,
    forecastWeekFrom: forecastData?.forecast_week_from,
    saveCubeWindow: tableState.appliedFilters.saveCubeWindow,
    rowSink: retention.rowSink
  });
  const intake = useWorkspaceIntake({
    page,
    gridRef: params.gridRef,
    metaData,
    tableControls: tableState.tableControls,
    forecastData,
    markdownStatus: tableState.appliedFilters.context?.markdown_status,
    saveCubeWindow: tableState.appliedFilters.saveCubeWindow,
    saveProducer: params.producer,
    rowSink: retention.rowSink
  });
  const isRecomputing = queue.isWriting || intake.intakePending;
  const actions = useWorkspaceActions({
    producer: params.producer,
    gridRef: params.gridRef,
    metaData,
    saveCubeId: tableState.appliedFilters.saveCubeId,
    forecastData,
    appliedFilters: tableState.appliedFilters,
    gridSource: intake.gridSource,
    intakeCacheRef: intake.intakeCacheRef,
    setSavedAt: tableState.setSavedAt,
    setToastState: tableState.setToastState,
    setRestateFPOSDialogOpen: tableState.setRestateFPOSDialogOpen,
    fetchForecastData,
    rowSink: retention.rowSink,
    setHasUnsavedEdits: data.setHasUnsavedEdits,
    hasUnsavedEdits: data.hasUnsavedEdits,
    isRecomputing
  });
  const edits = useEditTracking(queue, data.setHasUnsavedEdits);

  const { rowData, hasIntakeData } = useWorkspaceRows(
    forecastData,
    calendarView,
    algo.algoRows,
    retention
  );

  useRefreshCellsOnReload(forecastData, algo.algoRows, params.gridRef);

  // Spread: every picker-scoped field Apply snapshots is already named on
  // ``UseForecastWorkspaceParams``, so forwarding them one by one only risks
  // one going missing — which is how a half-applied snapshot happens.
  const handleApply = useHandleApply({
    ...params,
    fetchForecastData,
    intakeCacheRef: intake.intakeCacheRef,
    mountedRef,
    tableState
  });

  // Guarded so pages needn't touch workspace-owned appliedFilters.
  function clearForecastDataIfLoadedFrom(cubeId: string | undefined) {
    if (!cubeId || !tableState.appliedFilters.cubeIds.includes(cubeId)) return;
    data.clearForecastData();
    retention.clearRows();
    tableState.setSavedAt(null);
  }

  return {
    forecastData,
    metaData,
    rowData,
    hasIntakeData,
    availableIntakeSources: intake.availableIntakeSources,
    gridSource: intake.gridSource,
    isFetching: data.isFetching,
    isMetaDataFetching,
    isSaving: actions.isSaving,
    hasUnsavedEdits: data.hasUnsavedEdits,
    fillInOtbPending: actions.fillInOtbPending,
    intakePending: intake.intakePending,
    isRecomputing,
    restateFPOSPending: actions.restateFPOSPending,
    // The chrome state is forwarded whole; `ForecastWorkspace` names what a
    // page may read of it.
    ...tableState,
    handleApply,
    addAlgoForecast: algo.addAlgoForecast,
    algoFailures: algo.algoFailures,
    handleSave: actions.handleSave,
    saveGrid: actions.saveGrid,
    ...queue,
    ...edits,
    handleFillInOtb: actions.handleFillInOtb,
    handleRestateFPOS: actions.handleRestateFPOS,
    clearForecastDataIfLoadedFrom,
    gridStateRetention: retention
  };
}
